import { CustomModal } from "@/components/custom-modal";
import CustomTable from "@/components/CustomTable";
import CustomTabs from "@/components/CustomTabs";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Button } from "@/components/ui/button";

import { Skeleton } from "@/components/ui/skeleton";

import {
  getAllUserReferral,
  getUser,
  getUserBank,
  getUserTransactions,
  getUserTransactionsCount,
  getUserTransactionsVolume,
} from "@/services/Users.service";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { Ban, ChevronLeft, CircleCheck } from "lucide-react";
import { useState } from "react";
import { HiOutlineSlash } from "react-icons/hi2";
import { Link, useParams } from "react-router";
import { columnsReferrals, columnsTrans } from "./columns";

import Suspend from "./common/Suspend";
import Unsuspend from "./common/Unsuspend";
import UserActions from "./common/UserActions";
import General from "./comps/General";
import CustomCard from "@/components/CustomCard";
import BankComponent from "./comps/BankComponent";

import Notification from "./comps/Notification";
import ActivityLog from "./comps/ActivityLog";
import SendNotification from "./comps/SendNotification";

const Details = () => {
  const [page, setPage] = useState(1);
  let { id } = useParams();
  const { data, isFetching } = useQuery({
    queryKey: ["user", id],
    queryFn: () => getUser(id),
  });
  const { data: bank } = useQuery({
    queryKey: ["bank", id],
    queryFn: () => getUserBank(id),
  });
  const count = useQuery({
    queryKey: ["count", id],
    queryFn: () => getUserTransactionsCount(id),
  });
  const volume = useQuery({
    queryKey: ["volume", id],
    queryFn: () => getUserTransactionsVolume(id),
  });

  const { data: userTrans, isLoading: userTransLoading } = useInfiniteQuery({
    queryKey: ["customerTrans", id, page],
    queryFn: async ({ pageParam = page }) =>
      await getUserTransactions(pageParam, id),
    getNextPageParam: (lastPage: [], pages: []) => {
      if (lastPage.length === 0) return undefined;
      return pages.length + 1;
    },
  });

  const dataArr = userTrans?.pages
    ?.map((page) => page?.data?.data?.data)
    .flat();
  const meta = userTrans?.pages
    ?.map((page) => page?.data?.data?.meta)
    .flat()[0];

  const handlePageChange = (e: number) => {
    setPage(e);
  };

  const [filter, setFilter] = useState({
    search: "",
  });
  const {
    data: Referral,
    isLoading,
    isFetching: isFetchingReferral,
    refetch,
    hasNextPage,
    fetchNextPage,
  } = useInfiniteQuery({
    queryKey: ["usersReferral", filter, id],
    queryFn: async ({ pageParam = 1 }) =>
      await getAllUserReferral(pageParam, filter, id),
    getNextPageParam: (lastPage, pages) => {
      if (lastPage.length === 0) return undefined;
      return pages.length + 1;
    },

    refetchOnMount: true,
  });

  const dataArrReferral = Referral?.pages
    ?.map((page:any) => page.data.data.data)
    .flat();
  const metaReferral = Referral?.pages
    ?.map((page:any) => page.data.data.meta)
    .flat()[0];

  return (
    <section className="grid gap-10 px-6 pt-3">
      <div className="flex flex-col gap-8">
        <div>
          <Breadcrumb>
            <BreadcrumbList className="gap-2">
              <BreadcrumbItem>
                <Link
                  to="/dashboard/customers"
                  className="flex items-center gap-1 text-sm text-primary0"
                >
                  <ChevronLeft className="w-3 h-3" />
                  Customers
                </Link>
              </BreadcrumbItem>
              <BreadcrumbSeparator>
                <HiOutlineSlash />
              </BreadcrumbSeparator>
              <BreadcrumbItem>
                {isFetching ? (
                  <Skeleton className="w-full h-4" />
                ) : (
                  <BreadcrumbLink className="flex items-center gap-1 text-sm text-gray-60">
                    {data?.data?.data?.firstName} {data?.data?.data?.lastName}
                  </BreadcrumbLink>
                )}
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </div>

        <div className="flex items-center justify-between gap-4">
          {isFetching ? (
            <div className="flex items-center space-x-4">
              <Skeleton className="w-12 h-12 rounded-full" />
              <div className="space-y-2">
                <Skeleton className="h-4 w-[250px]" />
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <Avatar className="w-14 h-14">
                <AvatarImage />
                <AvatarFallback className="capitalize">
                  {data?.data?.data?.firstName[0]}{" "}
                  {data?.data?.data?.lastName[0]}
                </AvatarFallback>
              </Avatar>
              <div className="flex-col gap-2">
                <p className="text-2xl text-gray">
                  {data?.data?.data?.firstName} {data?.data?.data?.lastName}
                </p>
              </div>
            </div>
          )}
          <div className="flex gap-4">
            {data?.data?.data?.status !== "suspended" ? (
              <CustomModal
                button={
                  <Button className="bg-error-10 text-error0">
                    <Ban />
                    Block
                  </Button>
                }
                size="sm"
              >
                <Suspend
                  id={id}
                  name={
                    data?.data?.data?.firstName +
                    " " +
                    data?.data?.data?.lastName
                  }
                />
              </CustomModal>
            ) : (
              <CustomModal
                button={
                  <Button className="bg-green-0 text-green-3">
                    <CircleCheck />
                    Unblock
                  </Button>
                }
                size="sm"
              >
                <Unsuspend
                  id={id}
                  name={
                    data?.data?.data?.firstName +
                    " " +
                    data?.data?.data?.lastName
                  }
                />
              </CustomModal>
            )}
            <SendNotification data={data?.data?.data} />
            <UserActions
              id={id}
              payoutLocked={data?.data?.data?.payoutLocked}
              userExempt={data?.data?.data?.exemptFromKYC}
              kycStatus={data?.data?.data?.kycStatus}
            />
          </div>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-4">
        <CustomCard
          title="Total Transacted Volume"
          // ${volume?.data?.data?.data?.currency}
          mainTitle={volume?.data?.data?.data?.result?.toLocaleString(
            "en-US",
            {
              style: "currency",
              currency: "USD",
            }
          )}
          text="16"
          isLoading={volume?.isLoading}
        />
        <CustomCard
          title="Transaction Count"
          mainTitle={count?.data?.data?.data?.result}
          text="16"
          isLoading={count?.isLoading}
        />
        <CustomCard
          title="Referral Bonus"
          mainTitle={0}
          text="16"
          isLoading={count?.isLoading}
        />
        <CustomCard
          title="Referral Users"
          mainTitle={dataArrReferral?.length ?? 0}
          text="16"
          isLoading={isLoading}
        />
      </div>
      <div className="container mx-auto">
        <CustomTabs
          tabs={[
            "General",
            "Transactions",
            "Bank Accounts",
            "Referrals",
            "Activity Log",
            "Notification",
          ]}
          tabData={{
            General: <General data={data?.data?.data} />,
            Transactions: (
              <CustomTable
                columns={columnsTrans}
                data={dataArr ?? []}
                handlePageChange={handlePageChange}
                meta={meta}
                isLoading={userTransLoading}
                showPagination={true}
              />
          
            ),
            "Bank Accounts": <BankComponent data={bank?.data?.data} />,
            Referrals: (
              <CustomTable
                columns={columnsReferrals}
                data={dataArrReferral ?? []}
                handlePageChange={handlePageChange}
                meta={metaReferral}
                isLoading={isLoading}
                showPagination={true}
              />
            ),
            "Activity Log": <ActivityLog id={id} />,
            Notification: <Notification id={id} />,
          }}
        />
      </div>
    </section>
  );
};

export default Details;
